<template>
  <div
    v-if="!dismissed"
    class="pig-badge"
    :style="{ transform: `translateX(${dragX}px)`, opacity: dragOpacity }"
    @pointerdown="onPointerDown"
  >
    <a
      :href="igUrl"
      target="_blank"
      rel="noopener noreferrer nofollow"
      class="pig-link"
      :aria-label="`Instagram: @${username}`"
      @click="onLinkClick"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20">
        <path
          fill="currentColor"
          d="M17 1H3c-1.1 0-2 .9-2 2v14c0 1.101.9 2 2 2h14c1.1 0 2-.899 2-2V3c0-1.1-.9-2-2-2M9.984 15.523a5.54 5.54 0 0 0 5.538-5.539c0-.338-.043-.664-.103-.984H17v7.216a.69.69 0 0 1-.693.69H3.693a.69.69 0 0 1-.693-.69V9h1.549c-.061.32-.104.646-.104.984a5.54 5.54 0 0 0 5.539 5.539M6.523 9.984a3.461 3.461 0 1 1 6.922 0a3.461 3.461 0 0 1-6.922 0M16.307 6h-1.615A.694.694 0 0 1 14 5.308V3.691c0-.382.31-.691.691-.691h1.615c.384 0 .694.309.694.691v1.616c0 .381-.31.693-.693.693"
        />
      </svg>
      <span class="pig-username">@{{ username }}</span>
    </a>
  </div>
</template>

<script setup>
const props = defineProps({
  username: { type: String, required: true },
  url: { type: String, default: null },
});

const igUrl = computed(() => props.url || `https://www.instagram.com/${props.username}/`);

const dismissed = ref(false);
const dragX = ref(0);
const moved = ref(false);
let startX = 0;

// Reset the dismissed/dragged state whenever a different account is shown
// (e.g. navigating from the Chinese restaurant page to the Italian one).
watch(
  () => props.username,
  () => {
    dismissed.value = false;
    dragX.value = 0;
  }
);

// Drag tracking listens on window (rather than using setPointerCapture on the
// element) so it never interferes with the browser's native click-follow
// behaviour on the target="_blank" link — setPointerCapture on an ancestor
// was silently swallowing the click's default navigation.
function onPointerDown(e) {
  moved.value = false;
  startX = e.clientX;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp, { once: true });
}
function onPointerMove(e) {
  const delta = e.clientX - startX;
  if (Math.abs(delta) > 4) moved.value = true;
  dragX.value = Math.max(0, delta);
}
function onPointerUp() {
  window.removeEventListener('pointermove', onPointerMove);
  if (dragX.value > 56) {
    dismissed.value = true;
  }
  dragX.value = 0;
}
function onLinkClick(e) {
  if (moved.value) e.preventDefault();
}

const dragOpacity = computed(() => 1 - Math.min(dragX.value / 100, 0.75));
</script>

<style scoped>
.pig-badge {
  cursor: grab;
  touch-action: pan-y;
  transition: transform 0.18s ease, opacity 0.18s ease;
  user-select: none;
}
.pig-badge:active {
  cursor: grabbing;
  transition: none;
}
.pig-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  background-color: #e7bb7f;
  border-left: 2px solid #042333;
  border-bottom: 2px solid #042333;
  color: #042333;
  text-decoration: none;
}
.pig-username {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}

@media (max-width: 968px) {
  .pig-link {
    padding: 8px 0;
  }
  .pig-username {
    font-size: 14px;
  }
}
</style>
